import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { motion } from "motion/react";
import { companyTimeline, certifications, offices } from "@/mocks/about";

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-20 bg-primary relative overflow-hidden"
        style={{ minHeight: "380px" }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent" />
        <div className="relative w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 flex items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">About Fiber One</h1>
            <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base">
              Founded in October 2013 as a local AV company and over the years, has grown steadily within APAC & now opening in America. Our team of specialists have the capability to design, commission and deliver high-performance and robust AV systems applicable for these market segments:
            </p>
            <div className="w-16 h-1 bg-accent mx-auto mt-6 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-12 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Our Story</h2>
            <div className="w-16 h-1 bg-accent mx-auto rounded-full" />
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">
                Founded in October 2013 as a local AV company and over the years, has grown steadily within APAC & now opening in America. Our team of specialists have the capability to design, commission and deliver high-performance and robust AV systems applicable for these market segments:
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                  Corporate offices
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                  Government offices
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                  Schools and universities
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                  Convention and logistic centres
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                  Shopping malls
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                  Others
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="p-6 rounded-xl bg-gray-50 border border-gray-100 text-center">
                <div className="text-3xl font-bold text-accent mb-1">15+</div>
                <div className="text-xs text-gray-500">Years Experience</div>
              </div>
              <div className="p-6 rounded-xl bg-gray-50 border border-gray-100 text-center">
                <div className="text-3xl font-bold text-accent mb-1">500+</div>
                <div className="text-xs text-gray-500">Projects Delivered</div>
              </div>
              <div className="p-6 rounded-xl bg-gray-50 border border-gray-100 text-center">
                <div className="text-3xl font-bold text-accent mb-1">4</div>
                <div className="text-xs text-gray-500">Countries</div>
              </div>
              <div className="p-6 rounded-xl bg-gray-50 border border-gray-100 text-center">
                <div className="text-3xl font-bold text-accent mb-1">40+</div>
                <div className="text-xs text-gray-500">Team Members</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Our Journey</h2>
            <div className="w-16 h-1 bg-accent mx-auto rounded-full" />
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {companyTimeline.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.06 }}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
                className="group relative bg-white rounded-xl border border-gray-100 p-5 hover:border-accent/30 transition-all duration-300"
              >
                {/* Year badge */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center text-white font-bold text-sm shadow-sm">
                    {item.year}
                  </div>
                  <div className="h-px flex-1 bg-gray-200 group-hover:bg-accent/30 transition-colors" />
                </div>
                {/* Content */}
                <h3 className="text-sm font-bold text-gray-800 mb-1 leading-snug">
                  {item.title}
                </h3>
                {item.description && (
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {item.description}
                  </p>
                )}
                {/* Bottom accent bar */}
                <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-accent/0 group-hover:bg-accent/40 rounded-full transition-colors duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-12 md:py-20 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Industry Recognition</h2>
            <p className="text-sm text-gray-500 max-w-2xl mx-auto leading-relaxed">
              As a testament to our high standards and promise of providing quality service to our clients, below are some of the accreditations we have achieved over the past years.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
                className="group bg-gray-50 rounded-xl border border-gray-100 p-5 hover:border-accent/30 transition-all duration-300"
              >
                {/* Brand image */}
                <div className="w-full h-20 rounded-lg bg-white border border-gray-100 flex items-center justify-center mb-4 overflow-hidden group-hover:border-accent/20 transition-colors p-3"
                >
                  <img
                    src={cert.image}
                    alt={`${cert.name} certification`}
                    className="max-w-full max-h-full object-contain"
                    width={160}
                    height={60}
                    loading="lazy"
                  />
                </div>
                {/* Title */}
                <h3 className="text-sm font-bold text-accent mb-2">
                  {cert.name}
                </h3>
                {/* Description */}
                <p className="text-xs text-gray-500 leading-relaxed text-justify"
                >
                  {cert.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Offices */}
      <section className="py-12 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Our Offices</h2>
            <div className="w-16 h-1 bg-accent mx-auto rounded-full" />
            <p className="text-sm text-gray-500 mt-4 max-w-xl mx-auto">
              Regional presence across Singapore, the United States, and beyond.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-5xl mx-auto">
            {offices.map((office, index) => (
              <motion.div
                key={office.city}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="p-5 md:p-6 rounded-xl bg-gray-50 border border-gray-100 hover:border-accent/30 transition-all duration-300"
              >
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-map-pin-line text-accent text-lg" />
                  <h3 className="text-base font-bold text-gray-800">{office.city}</h3>
                </div>
                <p className="text-xs font-semibold text-accent mb-2">{office.name}</p>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">{office.address}</p>
                <div className="space-y-1 mb-3">
                  {office.phones.map((phone, i) => (
                    <p key={i} className="text-sm text-gray-700 font-medium">
                      <i className="ri-phone-line text-accent mr-1.5 text-sm"></i>{phone}
                    </p>
                  ))}
                </div>
                <div className="space-y-1">
                  {office.emails.map((email, i) => (
                    <a key={i} href={`mailto:${email}${email !== 'sales.sg@fiberoneglobal.com' ? '?cc=sales.sg@fiberoneglobal.com' : ''}`} className="block text-sm text-gray-700 hover:text-accent transition-colors">
                      <i className="ri-mail-line text-accent mr-1.5 text-sm"></i>{email}
                    </a>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}